import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ColDef, ModuleRegistry, RowSelectionModule } from 'ag-grid-community';
import { AuthRole, FindingsInsightsDto, SaasAiAgentKind } from '@ampsec/platform-client';

ModuleRegistry.registerModules([RowSelectionModule]);
import { Typography, Chip } from 'Components/atoms';
import { AgentHeader } from '../AgentHeader';
import { AgGridBase } from '../AgGridBase';
import {
    computeAutonomyScore,
    ConfiguredMcpServerRecord,
    HarnessInstallRecord,
} from 'Core-utils/staticData/staticAgents';
import { useAgentManagementAgents } from 'Hooks/useAgentManagementAgents';
import { useFindingsEngagementActions } from 'Hooks/useFindingsEngagementActions';
import ActionsTray from '../ActionsTray/ActionsTray';
import { getFindingsView, fetchSaasAiAgentSessions } from 'Apis/library';
import { normalizeFindingsFilterCriteria } from 'Core-utils/helpers/findingsUserAssociationFilter';
import { findingsOrgFilterFromOrgModel } from 'Core-utils/helpers/orgModelFilters';
import { RootState } from 'Rdx/store';
import type { SaasAiAgentSessionDto } from '@ampsec/platform-client';
import McpPolarAreaChart, { POLAR_COLORS } from './McpPolarAreaChart';
import './styles.scss';

// ---------------------------------------------------------------------------
// Hook — fetch AI_AGENT_MANAGEMENT findings by subject (APD-1948)
// ---------------------------------------------------------------------------

function useAgentFindings(agentId: string, userId?: string, refreshRevision = 0) {
    const [findings, setFindings] = useState<FindingsInsightsDto[]>([]);
    const [loading, setLoading] = useState(true);
    const organization = useSelector((state: RootState) => state.orgModel.org);

    useEffect(() => {
        let cancelled = false;

        const fetchFindings = async () => {
            setLoading(true);
            try {
                const filterCriteria = normalizeFindingsFilterCriteria({
                    ...findingsOrgFilterFromOrgModel(organization),
                });

                /* eslint-disable camelcase */
                const queryParams: Record<string, unknown> = {
                    subjectType: 'SAAS_AI_AGENT',
                    subjectId: agentId,
                    include: 'subjects',
                    limit: 250,
                    offset: 0,
                };
                if (userId) {
                    queryParams.user_id = { $in: [userId] };
                }
                /* eslint-enable camelcase */

                const page = await getFindingsView(filterCriteria, queryParams as any);
                if (cancelled) {
                    return;
                }
                setFindings(page?.data ?? []);
            } catch {
                if (!cancelled) {
                    setFindings([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchFindings();

        return () => {
            cancelled = true;
        };
    }, [agentId, userId, organization, refreshRevision]);

    return { findings, loading };
}

// ---------------------------------------------------------------------------
// Hook — fetch agent sessions (APD-1948)
// ---------------------------------------------------------------------------

function useAgentSessions(agentId: string) {
    const [sessions, setSessions] = useState<SaasAiAgentSessionDto[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Skip fetch if agentId is empty
        if (!agentId) {
            setLoading(false);
            return;
        }

        let cancelled = false;

        const fetchSessions = async () => {
            setLoading(true);
            try {
                const page = await fetchSaasAiAgentSessions({
                    aiAgentId: agentId,
                    limit: 50,
                    offset: 0,
                    sort: { discoveredAt: 'DESC' },
                });
                if (cancelled) {
                    return;
                }
                setSessions(page?.data ?? []);
            } catch {
                if (!cancelled) {
                    setSessions([]);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        fetchSessions();

        return () => {
            cancelled = true;
        };
    }, [agentId]);

    return { sessions, loading };
}

// ---------------------------------------------------------------------------
// Findings column defs — omits Issue Type, Provider, Finding Kind, Device
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Sessions column defs
// ---------------------------------------------------------------------------

const AGENT_SESSIONS_COLUMNS: ColDef<SaasAiAgentSessionDto>[] = [
    { field: 'extKey', headerName: 'Session ID', flex: 2, minWidth: 200 },
    { field: 'displayValue', headerName: 'Name', flex: 2, minWidth: 180 },
    {
        field: 'discoveredAt',
        headerName: 'Discovered',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : '-'),
    },
    {
        field: 'updatedAt',
        headerName: 'Last Updated',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString() : '-'),
    },
];

// ---------------------------------------------------------------------------
// Harness agents (APD-2006): installs + configured MCP servers
// ---------------------------------------------------------------------------

const formatDateTime = (value?: string) => (value ? new Date(value).toLocaleString() : '-');

const HARNESS_INSTALL_COLUMNS: ColDef<HarnessInstallRecord>[] = [
    { field: 'deviceSn', headerName: 'Device', flex: 1, minWidth: 140 },
    { field: 'rootPath', headerName: 'Location', flex: 3, minWidth: 260 },
    { field: 'version', headerName: 'Version', flex: 1, minWidth: 100 },
    {
        field: 'lastSeenAt',
        headerName: 'Last Seen',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => formatDateTime(params.value),
    },
];

const CONFIGURED_MCP_COLUMNS: ColDef<ConfiguredMcpServerRecord>[] = [
    { field: 'name', headerName: 'MCP Server', flex: 2, minWidth: 160 },
    { field: 'transport', headerName: 'Transport', flex: 1, minWidth: 100 },
    { field: 'target', headerName: 'Package / Host', flex: 2, minWidth: 200 },
    {
        field: 'envKeys',
        headerName: 'Credentials (names only)',
        flex: 2,
        minWidth: 200,
        valueGetter: (params) =>
            [...(params.data?.envKeys ?? []), ...(params.data?.headerKeys ?? [])].join(', ') || '-',
    },
    { field: 'scope', headerName: 'Scope', flex: 1, minWidth: 100 },
    {
        field: 'running',
        headerName: 'Running',
        flex: 1,
        minWidth: 100,
        valueFormatter: (params) => (params.value ? 'Yes' : '-'),
    },
    { field: 'deviceSn', headerName: 'Device', flex: 1, minWidth: 140 },
];

const AGENT_FINDINGS_COLUMNS: ColDef<FindingsInsightsDto>[] = [
    {
        field: 'severity',
        colId: 'severity',
        headerName: 'Severity',
        flex: 1,
        minWidth: 110,
        cellRenderer: (params: any) => {
            const row = params.data as FindingsInsightsDto;
            if (!row?.severity) {
                return null;
            }
            return (
                <div className="d-flex align-items-center h-100">
                    <Chip value={row.severity} variant="resolved_issue_variant" />
                </div>
            );
        },
    },
    {
        field: 'status',
        colId: 'finding_status',
        headerName: 'Status',
        flex: 1,
        minWidth: 110,
        cellRenderer: (params: any) => {
            const row = params.data as FindingsInsightsDto;
            if (!row?.status) {
                return null;
            }
            return <div className="d-flex align-items-center h-100">{row.status}</div>;
        },
    },
    {
        field: 'title',
        colId: 'finding_title',
        headerName: 'Title',
        flex: 2,
        minWidth: 180,
    },
    {
        field: 'discoveredAt',
        colId: 'discoveredAt',
        headerName: 'Discovered',
        flex: 1,
        minWidth: 140,
        cellRenderer: (params: any) => {
            const row = params.data as FindingsInsightsDto;
            if (!row?.discoveredAt) {
                return null;
            }
            return (
                <div className="d-flex align-items-center h-100">{new Date(row.discoveredAt).toLocaleDateString()}</div>
            );
        },
    },
    {
        field: 'description',
        colId: 'description',
        headerName: 'Description',
        flex: 3,
        minWidth: 220,
    },
];

// ---------------------------------------------------------------------------
// Attestation card — left 1/3 panel
// ---------------------------------------------------------------------------

interface AttestationCardProps {
    /** Routine name, e.g. "Ops Run (Weekdays)". Populated only for scheduled-automation sessions. */
    routineName?: string;
    /** Stable trigger ID for the routine, e.g. "trig_01JgoF4NZ5xRFzEQAEQZrJVY". */
    routineId?: string;
    purpose: string;
    accessesSensitiveData: boolean;
    criticality: string;
    runDuration: string;
    certifiedByOwner: boolean;
    certificationDate: string;
}

interface McpStatsCardProps {
    totalToolCalls: number;
    assistantTurns: number;
    userTurns: number;
    maxToolCallsBetweenUserMessages: number;
    mcpToolNames?: string[];
    /** Used to map service names → chart palette colors for the tool chips. */
    mcpToolCalls?: import('Core-utils/staticData/staticAgents').McpToolCallRecord[];
}

const AttestationRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="agent-detail-content__attestation-row d-flex align-items-start gap-2">
        <Typography variant="body3" color="text-medium-emphasis" className="agent-detail-content__attestation-label">
            {label}
        </Typography>
        <div className="agent-detail-content__attestation-value">{children}</div>
    </div>
);

const AttestationCard = ({
    routineName,
    routineId,
    purpose,
    accessesSensitiveData,
    criticality,
    runDuration,
    certifiedByOwner,
    certificationDate,
}: AttestationCardProps) => (
    <div className="agent-detail-content__attestation-card bg-text-black p-3 h-100 d-flex flex-column gap-3">
        <Typography variant="subtitle2" color="text-high-emphasis">
            Attestation
        </Typography>

        <AttestationRow label="Routine">
            <Typography variant="body3" color="text-high-emphasis">
                {routineName && routineId ? `${routineName} (${routineId})` : '-'}
            </Typography>
        </AttestationRow>

        <AttestationRow label="Purpose">
            <Typography variant="body3" color="text-high-emphasis">
                {purpose || '-'}
            </Typography>
        </AttestationRow>

        <AttestationRow label="Sensitive Data">
            <Chip
                value={accessesSensitiveData ? 'YES' : 'NO'}
                variant="resolved_issue_variant"
                variantValue={accessesSensitiveData ? 'HIGH' : 'LOW'}
            />
        </AttestationRow>

        <AttestationRow label="Criticality">
            <Chip value={criticality} variant="resolved_issue_variant" />
        </AttestationRow>

        <AttestationRow label="Run Duration">
            <Typography variant="body3" color="text-high-emphasis">
                {runDuration || '-'}
            </Typography>
        </AttestationRow>

        <AttestationRow label="Certified">
            <Chip
                value={certifiedByOwner ? 'YES' : 'NO'}
                variant="resolved_issue_variant"
                variantValue={certifiedByOwner ? 'LOW' : 'HIGH'}
            />
        </AttestationRow>

        <AttestationRow label="Cert. Date">
            <Typography variant="body3" color="text-high-emphasis">
                {certificationDate || 'Not certified'}
            </Typography>
        </AttestationRow>
    </div>
);

// ---------------------------------------------------------------------------
// MCP stats card — left sub-panel of the right column
// ---------------------------------------------------------------------------

const McpStatsCard = ({
    totalToolCalls,
    assistantTurns,
    userTurns,
    maxToolCallsBetweenUserMessages,
    mcpToolNames,
    mcpToolCalls,
}: McpStatsCardProps) => {
    /**
     * Map lowercase service name → chart palette entry so chips use the same
     * color as the corresponding polar-area segment.
     * e.g. mcpToolCalls[0].service = "Jira"  → POLAR_COLORS[0]
     *      mcpToolCalls[1].service = "GitHub" → POLAR_COLORS[1]
     */
    const serviceColorMap = new Map(
        (mcpToolCalls ?? []).map((tc, i) => [tc.service.toLowerCase(), POLAR_COLORS[i % POLAR_COLORS.length]]),
    );

    /** Extract the service prefix from a stripped tool name, e.g. "jira__create_issue" → "jira" */
    const chipBg = (tool: string): string => {
        const serviceKey = tool.split('__')[0].toLowerCase();
        return serviceColorMap.get(serviceKey)?.bg ?? 'rgba(255,255,255,0.07)';
    };

    const MCP_VISIBLE_CAP = 8;
    const [showAllMcpModal, setShowAllMcpModal] = useState(false);

    const chipStyle = (tool: string): React.CSSProperties => ({
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: '4px',
        background: chipBg(tool),
        fontSize: '0.6rem',
        fontFamily: 'monospace',
        color: 'inherit',
        whiteSpace: 'nowrap',
    });

    const viewAllChipStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '2px 7px',
        borderRadius: '4px',
        background: 'transparent',
        border: '1px solid rgba(255,255,255,0.25)',
        fontSize: '0.6rem',
        fontFamily: 'monospace',
        color: 'white',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
    };

    const visibleTools = mcpToolNames?.slice(0, MCP_VISIBLE_CAP) ?? [];
    const hasMore = (mcpToolNames?.length ?? 0) > MCP_VISIBLE_CAP;

    return (
        <div className="agent-detail-content__mcp-stats h-100 d-flex flex-column gap-3">
            <Typography variant="subtitle2" color="text-high-emphasis">
                Last Run
            </Typography>

            <AttestationRow label="Total Tool Calls">
                <Typography variant="body3" color="text-high-emphasis">
                    {totalToolCalls.toLocaleString()}
                </Typography>
            </AttestationRow>

            <AttestationRow label="Assistant Turns">
                <Typography variant="body3" color="text-high-emphasis">
                    {assistantTurns.toLocaleString()}
                </Typography>
            </AttestationRow>

            <AttestationRow label="User Turns">
                <Typography variant="body3" color="text-high-emphasis">
                    {userTurns.toLocaleString()}
                </Typography>
            </AttestationRow>

            <AttestationRow label="Max Tool Calls / User Msg">
                <Typography variant="body3" color="text-high-emphasis">
                    {maxToolCallsBetweenUserMessages.toLocaleString()}
                </Typography>
            </AttestationRow>

            {visibleTools.length ? (
                <div className="d-flex flex-wrap gap-1">
                    {visibleTools.map((tool) => (
                        <span key={tool} style={chipStyle(tool)}>
                            {tool.replace(/__/g, ' / ')}
                        </span>
                    ))}
                    {hasMore && (
                        <span
                            style={viewAllChipStyle}
                            onClick={() => setShowAllMcpModal(true)}
                            role="button"
                            aria-label="View all MCP tool calls"
                        >
                            View all MCP Tool calls
                        </span>
                    )}
                </div>
            ) : null}

            {/* ── All MCP Tool Calls modal ───────────────────────────── */}
            <Modal show={showAllMcpModal} onHide={() => setShowAllMcpModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1rem' }}>MCP Tool Calls</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap gap-1">
                        {(mcpToolNames ?? []).map((tool) => (
                            <span key={tool} style={chipStyle(tool)}>
                                {tool.replace(/__/g, ' / ')}
                            </span>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const AgentDetailContent = () => {
    const { id } = useParams<{ id: string }>();
    const { agents, loading } = useAgentManagementAgents();
    const agent = agents.find((a) => a.id === id || a.agentId === id);
    const auth = useSelector((state: RootState) => state.auth);

    const gridRef = useRef<any>(null);
    const [fetchRevision, setFetchRevision] = useState(0);
    const { findings, loading: findingsLoading } = useAgentFindings(agent?.id ?? '', agent?.userId, fetchRevision);
    const { sessions, loading: sessionsLoading } = useAgentSessions(agent?.id ?? '');

    const [selectedRows, setSelectedRows] = useState<FindingsInsightsDto[]>([]);
    const [actionsShow, setActionsShow] = useState(false);
    const engagementProps = useFindingsEngagementActions(selectedRows);

    const canSelectRows = auth?.user?.role === AuthRole.ADMIN || auth?.user?.role === AuthRole.OWNER;

    const onSelectionChanged = useCallback((params: any) => {
        const rows = params.api.getSelectedRows() as FindingsInsightsDto[];
        setSelectedRows(rows);
        setActionsShow(rows.length > 0);
    }, []);

    const handleActionsClose = useCallback(() => {
        setSelectedRows([]);
        setActionsShow(false);
        if (gridRef.current?.api) {
            gridRef.current.api.deselectAll();
        }
    }, []);

    const handleRefreshData = useCallback(() => {
        setFetchRevision((v) => v + 1);
    }, []);

    if (loading) {
        return null;
    }

    if (!agent) {
        return (
            <div className="bg-text-black p-4 card">
                <Typography variant="subtitle2" color="text-high-emphasis">
                    Agent not found
                </Typography>
                <Typography variant="body3" color="text-medium-emphasis" className="mt-2">
                    No agent with ID &ldquo;{id}&rdquo; exists in the agent inventory.
                </Typography>
            </div>
        );
    }

    return (
        <div className="agent-detail-content custom-scrollbar d-flex flex-column gap-3">
            {/* Top bar */}
            <AgentHeader
                agentId={agent.agentId}
                ownerName={agent.ownerName}
                ownerEmail={agent.ownerEmail}
                ownerDepartment={agent.ownerDepartment}
                vendor={agent.vendor}
                harness={agent.harness}
                model={agent.model}
                discoveredAt={agent.discoveredAt}
                lastRanAt={agent.lastRanAt}
                certifiedByOwner={agent.certifiedByOwner}
                autonomyScore={computeAutonomyScore(agent)}
            />

            {/* 1/3 + 2/3 split */}
            <div className="d-flex gap-3 agent-detail-content__split">
                {/* Left — attestation */}
                <div className="agent-detail-content__left">
                    <AttestationCard
                        routineName={agent.routineId ? agent.displayName : undefined}
                        routineId={agent.routineId}
                        purpose={agent.purpose}
                        accessesSensitiveData={agent.accessesSensitiveData}
                        criticality={agent.criticality}
                        runDuration={agent.runDuration}
                        certifiedByOwner={agent.certifiedByOwner}
                        certificationDate={agent.certificationDate}
                    />
                </div>

                {/* Right — single MCP tool calls card, split internally into stats + chart */}
                <div
                    className="agent-detail-content__right bg-text-black p-3 d-flex gap-3"
                    style={{ borderRadius: '0.5rem' }}
                >
                    <div className="agent-detail-content__mcp-stats-wrapper">
                        <McpStatsCard
                            totalToolCalls={agent.totalToolCalls}
                            assistantTurns={agent.assistantTurns}
                            userTurns={agent.userTurns}
                            maxToolCallsBetweenUserMessages={agent.maxToolCallsBetweenUserMessages}
                            mcpToolNames={agent.mcpToolNames}
                            mcpToolCalls={agent.mcpToolCalls}
                        />
                    </div>
                    <div className="agent-detail-content__mcp-chart-wrapper">
                        <McpPolarAreaChart toolCalls={agent.mcpToolCalls} />
                    </div>
                </div>
            </div>

            {agent.kind === SaasAiAgentKind.HARNESS ? (
                <>
                    {/* Endpoint-discovered harness: where it is installed and what it can reach */}
                    <AgGridBase.Root>
                        <AgGridBase.Header title="Installs" rowCount={agent.installs?.length ?? 0} />
                        <AgGridBase.Table<HarnessInstallRecord>
                            dataMode="client"
                            rowData={agent.installs ?? []}
                            columnDefs={HARNESS_INSTALL_COLUMNS}
                            agGridProps={{
                                getRowId: (params) => `${params.data?.deviceSn}:${params.data?.rootPath}`,
                            }}
                        />
                    </AgGridBase.Root>
                    <AgGridBase.Root>
                        <AgGridBase.Header
                            title="Configured MCP Servers"
                            rowCount={agent.configuredMcpServers?.length ?? 0}
                        />
                        <AgGridBase.Table<ConfiguredMcpServerRecord>
                            dataMode="client"
                            rowData={agent.configuredMcpServers ?? []}
                            columnDefs={CONFIGURED_MCP_COLUMNS}
                            agGridProps={{
                                getRowId: (params) =>
                                    `${params.data?.deviceSn}:${params.data?.scope}:${params.data?.name}`,
                            }}
                        />
                    </AgGridBase.Root>
                </>
            ) : (
                /* Sessions grid — all sessions for this agent */
                <AgGridBase.Root>
                    <AgGridBase.Header title="Sessions" rowCount={sessionsLoading ? undefined : sessions.length} />
                    <AgGridBase.Table<SaasAiAgentSessionDto>
                        dataMode="client"
                        rowData={sessionsLoading ? undefined : sessions}
                        columnDefs={AGENT_SESSIONS_COLUMNS}
                        agGridProps={{
                            getRowId: (params) => String(params.data?.id ?? ''),
                        }}
                    />
                </AgGridBase.Root>
            )}

            {/* Findings grid — AI_AGENT_MANAGEMENT findings matched by subject */}
            <AgGridBase.Root>
                <AgGridBase.Header title="Findings" rowCount={findingsLoading ? undefined : findings.length} />
                <AgGridBase.Table<FindingsInsightsDto>
                    ref={gridRef}
                    dataMode="client"
                    rowData={findingsLoading ? undefined : findings}
                    columnDefs={AGENT_FINDINGS_COLUMNS}
                    agGridProps={{
                        getRowId: (params) => String(params.data?.id ?? ''),
                        rowSelection: canSelectRows ? { mode: 'multiRow' } : undefined,
                        onSelectionChanged,
                    }}
                />
            </AgGridBase.Root>
            {actionsShow && (
                <ActionsTray
                    showActionsTray={actionsShow}
                    CountOfSelectedItems={selectedRows.length}
                    isFindingsGrid={false}
                    selectedRows={selectedRows}
                    onActionsTrayClose={handleActionsClose}
                    onClear={handleActionsClose}
                    onActionShow={handleActionsClose}
                    onRefreshData={handleRefreshData}
                    clearSelectedRows={handleActionsClose}
                    {...engagementProps}
                />
            )}
        </div>
    );
};

export default AgentDetailContent;
